import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';
import './UserDetailPage.css';
import './ReportDetailPage.css';

function ReportDetailPage() {
    const { chatRoomId } = useParams();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!currentUser) return;
        const fetchReportDetails = async () => {
            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/reports/chatroom/${chatRoomId}`,{
                    // [수정] headers에 인증 토큰을 추가합니다.
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                });
                const data = await response.json();
                if (response.ok) {
                    setReportData(data);
                }
            } catch (error) {
                console.error("신고 상세 정보 로딩 오류:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchReportDetails();
    }, [chatRoomId, currentUser]);

    const handleSuspendUser = async (userToSuspend, durationDays, durationText) => {
        setIsDropdownOpen(false);
        if (!userToSuspend) return alert("피신고자 정보를 찾을 수 없습니다.");

        if (window.confirm(`${userToSuspend.userName} 사용자에게 '${durationText}' 조치를 적용하시겠습니까?`)) {
            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/users/${userToSuspend._id}/suspend`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${idToken}`
                    },
                    body: JSON.stringify({ durationDays })
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                alert(data.message);
            } catch (error) {
                alert(`오류가 발생했습니다: ${error.message}`);
            }
        }
    };

    const handleResolveReport = async () => {
        setIsDropdownOpen(false);
        if (window.confirm("이 채팅방의 모든 신고를 '처리 완료' 상태로 변경하시겠습니까?")) {
            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/reports/chatroom/${chatRoomId}/resolve`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${idToken}`
                    },
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                alert(data.message);
                navigate('/admin/reports');
            } catch (error) {
                alert(`오류: ${error.message}`);
            }
        }
    };

    // [추가] 신고를 '처리 대기' 상태로 되돌리는 함수
    const handleReopenReport = async () => {
        if (window.confirm("이 신고를 다시 '처리 대기' 상태로 되돌리시겠습니까?")) {
            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/reports/chatroom/${chatRoomId}/reopen`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${idToken}`
                    },
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);

                alert(data.message);
                setReportData(prevData => ({ ...prevData, status: 'PENDING' }));
            } catch (error) {
                alert(`오류: ${error.message}`);
            }
        }
    };

    if (loading) return <div className="report-detail-container">로딩 중...</div>;
    if (!reportData) return <div className="report-detail-container">신고 정보를 찾을 수 없습니다.</div>;

    const { reportingUsers, reportedUsers, contextMessages, status, createdAt } = reportData;
    
    const allInvolvedUsers = [...(reportingUsers || []), ...(reportedUsers || [])].reduce((acc, user) => {
        if (user && !acc.find(u => u._id === user._id)) {
            acc.push(user);
        }
        return acc;
    }, []);

    return (
        <div className="report-detail-container">
            <h1>신고 상세 내역</h1>
            <div className="report-summary-card">
                <div className="summary-item">
                    <strong>신고자</strong>
                    {reportingUsers?.map(user => (
                        <div key={user._id}>
                            <Link to={`/admin/users/${user._id}`}>{user.userName} ({user.email})</Link>
                        </div>
                    ))}
                </div>
                <div className="summary-item">
                    <strong>피신고자</strong>
                    {reportedUsers?.map(user => (
                        <div key={user._id}>
                            <Link to={`/admin/users/${user._id}`}>{user.userName} ({user.email})</Link>
                        </div>
                    ))}
                </div>
                <div className="summary-item">
                    <strong>신고일</strong>
                    {new Date(createdAt).toLocaleString()}
                </div>
                <div className="summary-item">
                    <strong>상태</strong>
                    {status}
                </div>
            </div>

            {/* [수정] 신고 상태에 따라 다른 버튼을 보여줌 */}
            <div className="report-actions">
                {status === 'PENDING' ? (
                    <div className="dropdown">
                        <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="suspend-button">
                            조치 선택 ▾
                        </button>
                        {isDropdownOpen && (
                            <div className="dropdown-menu">
                                {allInvolvedUsers.map(user => (
                                    <React.Fragment key={user._id}>
                                        <div className="dropdown-item" style={{fontWeight: 'bold', backgroundColor: '#f0f0f0'}}>{user.userName} 제재</div>
                                        <button className="dropdown-item" onClick={() => handleSuspendUser(user, 1, '1일 이용 정지')}>&nbsp;&nbsp;↳ 1일 정지</button>
                                        <button className="dropdown-item" onClick={() => handleSuspendUser(user, 7, '7일 이용 정지')}>&nbsp;&nbsp;↳ 7일 정지</button>
                                        <button className="dropdown-item" onClick={() => handleSuspendUser(user, 99999, '영구 정지')}>&nbsp;&nbsp;↳ 영구 정지</button>
                                    </React.Fragment>
                                ))}
                                <hr style={{ margin: '5px 0' }} />
                                <button className="dropdown-item" onClick={handleResolveReport}>신고 처리 완료</button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button onClick={handleReopenReport} className="reopen-button">
                        '처리 대기' 상태로 되돌리기
                    </button>
                )}
            </div>

            <div className="context-messages-container">
                <h2>전체 대화 내용</h2>
                {contextMessages?.map(msg => {
                    const isReported = reportedUsers.some(u => u._id === msg.senderId.toString());
                    const messageClass = isReported ? 'partner-message' : 'my-message';

                    return (
                        <div key={msg.messageId} className={`context-message ${messageClass} ${msg.isReportedTarget ? 'reported-target' : ''}`}>
                            <div className="sender-info">{msg.senderInfo}</div>
                            <p className="message-text">{msg.messageText}</p>
                            <span className="timestamp">{new Date(msg.timestamp).toLocaleString()}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ReportDetailPage;